import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import GridToolbarNoExport from "../../../common/GridToolbarNoExport";
import AppDataGrid from "../../../common/AppDataGrid";
import useFetchEligibility from "../hooks/EligibilityHook";

const EligibilityReport = ({ schoolId }) => {
  const { data, loading } = useFetchEligibility(schoolId);
  const [searchText, setSearchText] = useState("");
  const [sortModel, setSortModel] = useState([
    { field: "Last_Name", sort: "asc" },
  ]);

  const columns = [
    { field: "Tartan_ID", headerName: "Tartan_ID", flex: 1 },
    { field: "High_School", headerName: "High School", flex: 1 },
    { field: "First_Name", headerName: "First Name", flex: 1 },
    { field: "Last_Name", headerName: "Last Name", flex: 1 },
    { field: "Email", headerName: "Email", flex: 1 },
    { field: "SSID", headerName: "SSID", flex: 1 },
    { field: "School_District", headerName: "District", flex: 1 },
    { field: "Graduation_Year", headerName: "Grad Year", flex: 1 },
    { field: "English_Status", headerName: "English Status", flex: 1 },
    {
      field: "Highest_English_Equivalency",
      headerName: "Highest English",
      flex: 1,
    },
    { field: "Math_Status", headerName: "Math Status", flex: 1 },
    {
      field: "Highest_Math_Equivalency",
      headerName: "Highest Math",
      flex: 1,
    },
    { field: "NCCCP", headerName: "NCCCP", flex: 1 },
    { field: "Tested", headerName: "Tested", flex: 1 },
    { field: "Academic_Standing", headerName: "Academic Standing", flex: 1 },
    { field: "GPA", headerName: "GPA", flex: 1 },
  ];

  // Generate a unique ID for each row if it doesn't exist.
  const rowsWithIds = data.map((row, index) => ({
    ...row,
    // prefer server-provided id, then Tartan_ID, then generated id
    id: row.id ?? row.Tartan_ID ?? `row-${index}`,
  }));

  // Filter rows based on search text
  const filteredRows = rowsWithIds.filter((row) => {
    if (!searchText) return true;

    const searchLower = searchText.toLowerCase();
    return Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchLower),
    );
  });

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  // Export every field present on each row — not just the columns shown
  // in the grid — since the underlying report rows can carry more fields
  // than this view chooses to display. Columns that ARE shown in the grid
  // come first, in the same order/labels as the on-screen report; any
  // extra fields the grid doesn't display are appended after, under their
  // raw field name. The synthetic `id` added for the grid's row key is
  // excluded — it's not a real report field.
  const handleExportExcel = () => {
    if (filteredRows.length === 0) return;

    const gridFields = columns.map((col) => col.field);
    const extraFields = Array.from(
      new Set(filteredRows.flatMap((row) => Object.keys(row))),
    ).filter((field) => field !== "id" && !gridFields.includes(field));

    const dataForSheet = filteredRows.map((row) => {
      const rowData = {};
      columns.forEach((col) => {
        rowData[col.headerName || col.field] = row[col.field] ?? "";
      });
      extraFields.forEach((field) => {
        rowData[field] = row[field] ?? "";
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `Eligibility_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        title="Eligibility Report"
        action={
          <Tooltip title="Download to Excel">
            <IconButton
              onClick={handleExportExcel}
              sx={{ color: "white" }}
              aria-label="Download to Excel"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        }
        sx={{ backgroundColor: "#AC1A2F", color: "white" }}
      />
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search"
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#AC1A2F",
                },
                "&:hover fieldset": {
                  borderColor: "#AC1A2F",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#AC1A2F",
                },
              },
            }}
          />
        </Box>
        <AppDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          // control sortModel to prevent the grid entering the 'none' state
          sortModel={sortModel}
          onSortModelChange={(newModel) => {
            if (!newModel || newModel.length === 0) {
              // restore default sort instead of allowing 'none'
              setSortModel([{ field: "Last_Name", sort: "asc" }]);
            } else {
              setSortModel(newModel);
            }
          }}
          slots={{ toolbar: GridToolbarNoExport }}
          autosizeOnMount
          autoHeight
          pagination
          pageSize={25}
          pageSizeOptions={[25]}
        />
      </CardContent>
    </Card>
  );
};

export default EligibilityReport;
